import { ContactUs, CopyrightFooter } from 'app/components'
import { Container, IAppState } from 'app/containers/Container'
import * as React from 'react'
import { Col, Row } from 'react-bootstrap'

interface IEmployeeCompensationFilterOption {
  label: string
  value?: number | string | null
  id?: number | null
  code?: string | null
  sortOrder?: number
}

interface IEmployeeCompensationRow {
  rank: number
  totalComp: number
  name: string
  title: string
  foundationAssets: string | null
  foundationName: string
  location: string
  year: number
}

interface IEmployeeCompensationApiResponse {
  filters: {
    selected: {
      year: number | string | null
      assetLevelId: number | null
      state: string | null
      city: string | null
    }
    years: IEmployeeCompensationFilterOption[]
    assetLevels: IEmployeeCompensationFilterOption[]
    states: IEmployeeCompensationFilterOption[]
    cities: IEmployeeCompensationFilterOption[]
  }
  table: {
    limit: number
    rows: IEmployeeCompensationRow[]
  }
}

interface IEmployeeCompensationState {
  filters: {
    years: IEmployeeCompensationFilterOption[]
    assetLevels: IEmployeeCompensationFilterOption[]
    states: IEmployeeCompensationFilterOption[]
    cities: IEmployeeCompensationFilterOption[]
  }
  selectedYear: number | string
  selectedAssetLevelId: number | ''
  selectedState: string
  selectedCity: string
  rows: IEmployeeCompensationRow[]
  hoveredCellKey: string
  isLoading: boolean
  error: string
}

interface ICompensationPageConfig {
  apiUrl: string
  pageTitle: string
  tableSubtitle: string
  tableHeading: string
  loadingLabel: string
  emptyLabel: string
  errorMessage: string
  filterStateOption?: (option: IEmployeeCompensationFilterOption) => boolean
}

interface IEmployeeCompensationProps {
  config?: ICompensationPageConfig
}

const EMPLOYEE_COMPENSATION_CONFIG: ICompensationPageConfig = {
  apiUrl: 'https://9jtty2utj2.execute-api.us-east-1.amazonaws.com/employee-compensation',
  pageTitle: 'Employee Compensation Table',
  tableSubtitle: 'Highest Paid Employees',
  tableHeading: 'Highest Paid Employees',
  loadingLabel: 'Loading employee compensation...',
  emptyLabel: 'No employees matched the current filters.',
  errorMessage: 'We were unable to load employee compensation data right now.',
}
const MAX_ROWS = 50
const ALLOWED_YEARS = {
  mostRecent: true,
  2024: true,
  2023: true,
  2022: true,
}
const palette = {
  page: '#f6f8fb',
  card: '#ffffff',
  ink: '#36485c',
  muted: '#708090',
  border: '#d8dee8',
  heading: '#6e7681',
  accent: '#4e123f',
  accentText: '#ffffff',
  tableStripe: '#eef1f5',
  shadow: '0 20px 45px rgba(44, 68, 96, 0.10)',
}

const sortAssetLevels = (left: IEmployeeCompensationFilterOption, right: IEmployeeCompensationFilterOption) => {
  const leftOrder = typeof left.sortOrder === 'number' ? left.sortOrder : -1
  const rightOrder = typeof right.sortOrder === 'number' ? right.sortOrder : -1

  if (leftOrder === rightOrder) {
    return left.label.localeCompare(right.label)
  }

  return leftOrder - rightOrder
}

const isVisibleYearOption = (option: IEmployeeCompensationFilterOption) => {
  const optionValue = typeof option.value === 'undefined' || option.value === null ? option.label : option.value

  return ALLOWED_YEARS[String(optionValue)] === true
}

class EmployeeCompensation extends Container<IEmployeeCompensationProps, IEmployeeCompensationState> {
  state: IEmployeeCompensationState = {
    filters: {
      years: [],
      assetLevels: [],
      states: [],
      cities: [],
    },
    selectedYear: 'mostRecent',
    selectedAssetLevelId: '',
    selectedState: '',
    selectedCity: '',
    rows: [],
    hoveredCellKey: '',
    isLoading: true,
    error: '',
  }

  mapAppStateToContainerState(appState: IAppState): IEmployeeCompensationState {
    return this.state || {
      filters: {
        years: [],
        assetLevels: [],
        states: [],
        cities: [],
      },
      selectedYear: 'mostRecent',
      selectedAssetLevelId: '',
      selectedState: '',
      selectedCity: '',
      rows: [],
      hoveredCellKey: '',
      isLoading: true,
      error: '',
    }
  }

  componentDidMount() {
    this.loadEmployeeCompensation()
  }

  getConfig() {
    return this.props.config || EMPLOYEE_COMPENSATION_CONFIG
  }

  buildUrl(year: number | string, assetLevelId: number | '', state: string, city: string) {
    const params: string[] = []
    const apiUrl = this.getConfig().apiUrl

    if (year !== '' && year !== null && typeof year !== 'undefined') {
      params.push('year=' + encodeURIComponent(String(year)))
    }

    if (assetLevelId !== '') {
      params.push('assetLevelId=' + encodeURIComponent(String(assetLevelId)))
    }

    if (state) {
      params.push('state=' + encodeURIComponent(state))
    }

    if (city) {
      params.push('city=' + encodeURIComponent(city))
    }

    if (params.length === 0) {
      return apiUrl
    }

    return apiUrl + '?' + params.join('&')
  }

  loadEmployeeCompensation(year?: number | string, assetLevelId?: number | '', state?: string, city?: string) {
    const nextYear = typeof year !== 'undefined' ? year : this.state.selectedYear
    const nextAssetLevelId = typeof assetLevelId !== 'undefined' ? assetLevelId : this.state.selectedAssetLevelId
    const nextState = typeof state !== 'undefined' ? state : this.state.selectedState
    const nextCity = typeof city !== 'undefined' ? city : this.state.selectedCity

    this.setState({
      isLoading: true,
      error: '',
      selectedYear: nextYear,
      selectedAssetLevelId: nextAssetLevelId,
      selectedState: nextState,
      selectedCity: nextCity,
    })

    fetch(this.buildUrl(nextYear, nextAssetLevelId, nextState, nextCity))
      .then((resp) => resp.json())
      .then((json: IEmployeeCompensationApiResponse) => {
        const filters = json.filters || {} as any
        const selected = filters.selected || { year: 'mostRecent', assetLevelId: null, state: null, city: null }
        const years = filters.years ? filters.years.filter((option) => isVisibleYearOption(option)) : []
        const assetLevels = filters.assetLevels ? filters.assetLevels.slice().sort(sortAssetLevels) : []
        const filterStateOption = this.getConfig().filterStateOption || ((option) => option.code !== 'ZZ')
        const states = filters.states ? filters.states.filter(filterStateOption) : []
        const cities = filters.cities || []
        const rows = json.table && json.table.rows ? json.table.rows.slice(0, MAX_ROWS) : []

        this.setState({
          filters: {
            years,
            assetLevels,
            states,
            cities,
          },
          selectedYear: typeof selected.year === 'undefined' || selected.year === null || !ALLOWED_YEARS[String(selected.year)]
            ? 'mostRecent'
            : selected.year,
          selectedAssetLevelId: selected.assetLevelId === null ? '' : selected.assetLevelId,
          selectedState: selected.state || '',
          selectedCity: selected.city || '',
          rows,
          isLoading: false,
          error: '',
        })
      })
      .catch(() => {
        this.setState({
          isLoading: false,
          error: this.getConfig().errorMessage,
        })
      })
  }

  handleYearChange(event) {
    this.loadEmployeeCompensation(event.currentTarget.value, this.state.selectedAssetLevelId, this.state.selectedState, this.state.selectedCity)
  }

  handleAssetLevelChange(event) {
    const value = event.currentTarget.value === '' ? '' : Number(event.currentTarget.value)
    this.loadEmployeeCompensation(this.state.selectedYear, value, this.state.selectedState, this.state.selectedCity)
  }

  handleStateChange(event) {
    this.loadEmployeeCompensation(this.state.selectedYear, this.state.selectedAssetLevelId, event.currentTarget.value, '')
  }

  handleCityChange(event) {
    this.loadEmployeeCompensation(this.state.selectedYear, this.state.selectedAssetLevelId, this.state.selectedState, event.currentTarget.value)
  }

  getTableLabel() {
    const selectedYear = this.state.selectedYear
    const years = this.state.filters.years

    for (const year of years) {
      if (String(year.value) === String(selectedYear)) {
        return year.label
      }
    }

    return selectedYear === 'mostRecent' ? 'Most Recent' : String(selectedYear)
  }

  formatCompensation(value: number) {
    if (!value && value !== 0) {
      return 'N/A'
    }

    if (value >= 1000000) {
      return '$' + (value / 1000000).toFixed(1) + ' million'
    }

    return '$' + value.toLocaleString()
  }

  formatHoverValue(value: string) {
    return value.replace(/(\.\.\.|…)+\s*$/, '').trim()
  }

  renderTruncatedCell(cellKey: string, value: string, width: string) {
    const isHovered = this.state.hoveredCellKey === cellKey
    const hoverValue = this.formatHoverValue(value)

    return (
      <div
        style={{ position: 'relative' }}
        onMouseEnter={() => this.setState({ hoveredCellKey: cellKey })}
        onMouseLeave={() => this.setState({ hoveredCellKey: '' })}
      >
        <div
          style={{
            maxWidth: width,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {value}
        </div>
        {isHovered ?
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: 'calc(100% + 8px)',
              zIndex: 5,
              minWidth: '220px',
              maxWidth: '420px',
              padding: '12px 14px',
              borderRadius: '14px',
              backgroundColor: '#233447',
              color: '#ffffff',
              boxShadow: '0 16px 35px rgba(18, 31, 48, 0.28)',
              fontSize: '1.6rem',
              lineHeight: '1.45',
              whiteSpace: 'normal',
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: '-8px',
                left: '20px',
                width: 0,
                height: 0,
                borderLeft: '8px solid transparent',
                borderRight: '8px solid transparent',
                borderBottom: '8px solid #233447',
              }}
            />
            {hoverValue}
          </div> :
          null}
      </div>
    )
  }

  renderFilterBlock(title: string, value: number | string, options: IEmployeeCompensationFilterOption[], onChange) {
    return (
      <section
        style={{
          backgroundColor: palette.card,
          border: '1px solid ' + palette.border,
          borderRadius: '20px',
          boxShadow: palette.shadow,
          padding: '22px 22px 24px',
          minHeight: '100%',
        }}
      >
        <h3
          style={{
            color: palette.heading,
            marginTop: 0,
            marginBottom: '16px',
            fontSize: '2.3rem',
            fontWeight: 700,
            textDecoration: 'underline',
            textUnderlineOffset: '6px',
          }}
        >
          {title}
        </h3>
        <select
          value={value}
          onChange={onChange}
          style={{
            width: '100%',
            height: '54px',
            border: '1px solid #aeb8c4',
            borderRadius: '10px',
            padding: '0 14px',
            fontSize: '1.8rem',
            color: palette.ink,
            backgroundColor: '#fff',
          }}
        >
          {options.map((option, index) => {
            const optionValue = typeof option.value !== 'undefined'
              ? option.value
              : typeof option.id !== 'undefined'
                ? option.id
                : option.code

            return (
              <option key={title + '-' + index + '-' + String(optionValue)} value={optionValue === null ? '' : optionValue}>
                {option.label}
              </option>
            )
          })}
        </select>
      </section>
    )
  }

  renderTable() {
    const rows = this.state.rows
    const config = this.getConfig()

    if (this.state.isLoading) {
      return (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: palette.muted, fontSize: '1.8rem' }}>
          <i className="fa fa-cog fa-spin" /> {config.loadingLabel}
        </div>
      )
    }

    if (this.state.error) {
      return (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: '#9b2c2c', fontSize: '1.8rem' }}>
          {this.state.error}
        </div>
      )
    }

    if (rows.length === 0) {
      return (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: palette.muted, fontSize: '1.8rem' }}>
          {config.emptyLabel}
        </div>
      )
    }

    return (
      <div style={{ overflowX: 'auto' }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'separate',
            borderSpacing: 0,
            border: '2px solid #1f2630',
            borderRadius: '16px',
            overflow: 'hidden',
            backgroundColor: palette.card,
          }}
        >
          <thead>
            <tr>
              <th
                colSpan={7}
                style={{
                  backgroundColor: palette.accent,
                  color: palette.accentText,
                  textAlign: 'center',
                  fontSize: '2rem',
                  fontWeight: 700,
                  padding: '14px 10px',
                }}
              >
                {config.tableHeading} - {this.getTableLabel()} (Top {MAX_ROWS})
              </th>
            </tr>
            <tr style={{ backgroundColor: '#ffffff' }}>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '1.8rem', color: palette.ink, textDecoration: 'underline', whiteSpace: 'nowrap' }}>Rank</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '1.8rem', color: palette.ink, textDecoration: 'underline', whiteSpace: 'nowrap' }}>Total Comp</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '1.8rem', color: palette.ink, textDecoration: 'underline', whiteSpace: 'nowrap' }}>Name</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '1.8rem', color: palette.ink, textDecoration: 'underline', whiteSpace: 'nowrap' }}>Title</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '1.8rem', color: palette.ink, textDecoration: 'underline', whiteSpace: 'nowrap' }}>Foundation Name</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '1.8rem', color: palette.ink, textDecoration: 'underline', whiteSpace: 'nowrap' }}>Location</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '1.8rem', color: palette.ink, textDecoration: 'underline', whiteSpace: 'nowrap' }}>Year</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={row.rank + '-' + row.name + '-' + row.foundationName} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : palette.tableStripe }}>
                <td style={{ padding: '12px 16px', fontSize: '1.8rem', color: palette.ink, whiteSpace: 'nowrap' }}>{row.rank}</td>
                <td style={{ padding: '12px 16px', fontSize: '1.8rem', color: palette.ink, whiteSpace: 'nowrap', fontWeight: 700 }}>{this.formatCompensation(row.totalComp)}</td>
                <td style={{ padding: '12px 16px', fontSize: '1.8rem', color: palette.ink }}>
                  {this.renderTruncatedCell('name-' + row.rank, row.name, '170px')}
                </td>
                <td style={{ padding: '12px 16px', fontSize: '1.8rem', color: palette.ink }}>
                  {this.renderTruncatedCell('title-' + row.rank, row.title, '180px')}
                </td>
                <td style={{ padding: '12px 16px', fontSize: '1.8rem', color: palette.ink }}>
                  {this.renderTruncatedCell('foundation-' + row.rank, row.foundationName, '240px')}
                </td>
                <td style={{ padding: '12px 16px', fontSize: '1.8rem', color: palette.ink, whiteSpace: 'nowrap' }}>
                  {this.renderTruncatedCell('location-' + row.rank, row.location, '170px')}
                </td>
                <td style={{ padding: '12px 16px', fontSize: '1.8rem', color: palette.ink, whiteSpace: 'nowrap' }}>{row.year}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  render() {
    const { filters, selectedYear, selectedAssetLevelId, selectedState, selectedCity } = this.state
    const config = this.getConfig()

    return (
      <section>
        <section
          className="margin-y-lg animated fadeIn"
          style={{
            width: 'calc(100vw - 48px)',
            maxWidth: '1440px',
            margin: '0 auto',
            overflowX: 'hidden',
            background: 'linear-gradient(180deg, #ffffff 0%, ' + palette.page + ' 100%)',
            borderRadius: '30px',
            padding: '28px 24px 34px',
          }}
        >
          <h1
            style={{
              textAlign: 'center',
              color: palette.heading,
              fontSize: '3.5rem',
              fontWeight: 700,
              marginTop: '6px',
              marginBottom: '12px',
            }}
          >
            {config.pageTitle}
          </h1>

          <h2
            style={{
              textAlign: 'center',
              color: palette.heading,
              fontSize: '3rem',
              fontWeight: 700,
              marginTop: 0,
              marginBottom: '34px',
              textDecoration: 'underline',
              textUnderlineOffset: '7px',
            }}
          >
            {config.tableSubtitle}
          </h2>

          <Row>
            <Col md={3} sm={6} xs={12} style={{ marginBottom: '22px' }}>
              {this.renderFilterBlock('Foundation Size', selectedAssetLevelId, filters.assetLevels, this.handleAssetLevelChange.bind(this))}
            </Col>
            <Col md={3} sm={6} xs={12} style={{ marginBottom: '22px' }}>
              {this.renderFilterBlock('State', selectedState, filters.states, this.handleStateChange.bind(this))}
            </Col>
            <Col md={3} sm={6} xs={12} style={{ marginBottom: '22px' }}>
              {this.renderFilterBlock('City', selectedCity, filters.cities, this.handleCityChange.bind(this))}
            </Col>
            <Col md={3} sm={6} xs={12} style={{ marginBottom: '22px' }}>
              {this.renderFilterBlock('Year', selectedYear, filters.years, this.handleYearChange.bind(this))}
            </Col>
          </Row>

          <section
            style={{
              margin: '8px auto 0',
              backgroundColor: palette.card,
              border: '1px solid ' + palette.border,
              borderRadius: '24px',
              boxShadow: palette.shadow,
              padding: '18px',
            }}
          >
            {this.renderTable()}
          </section>
        </section>

        <footer>
          <ContactUs />
          <CopyrightFooter />
        </footer>
      </section>
    )
  }
}

export {
  EmployeeCompensation
}



// WEBPACK FOOTER //
// ./src/app/containers/EmployeeCompensation/index.tsx